import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Plus, Search, Pencil, Trash2, Play, X, LayoutList, LayoutGrid, ChevronRight,
    BookOpen, CircleCheck, CirclePause, UserX, CalendarClock, Radio, GraduationCap, Eye, EyeOff,
    ImagePlus, History, Users,
} from 'lucide-react';
import HxSelect from '../../../components/ui/HxSelect';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../services/firebase';
import {
    getInstructors, getCourses, getSchedulesIncludingHidden, subscribeToCalendarCourses, createCourse, updateCourse,
    deleteCourse, cascadeRenameCourseEvents, setCourseSchedulesHidden,
    getCourseSchedulesAll, setCourseSchedulesHiddenWhere, eventGroupOf,
} from '../services';
import toast from 'react-hot-toast';

// ============================================================================
// Cursos del Calendario (única pantalla para crear/editar cursos; la del
// Generador quedó desactivada). Estilo beta.hingenia.com: tabla o tarjetas, y
// la edición se abre como panel a la derecha que divide la pantalla (sin popup),
// así se puede pasar de un curso a otro sin cerrar nada.
// - Portada del curso: courses/{id}/cover_*.jpg en Storage → campo coverUrl.
// - Visibilidad parcial: cada grupo y tipo (MC / clases) se oculta desde hoy o se
//   restaura por separado; desactivar el curso completo oculta todo.
// - "Programar con IA" se quitó por ahora (CourseSchedulingModal se conserva).
// ============================================================================

// ── Opciones de hora en intervalos de 30 min (formato 12 hrs) ────
const TIME_OPTIONS = [];
for (let h = 6; h <= 23; h++) {
    for (let m of ['00', '30']) {
        const hour12 = h % 12 || 12;
        const ampm = h < 12 ? 'AM' : 'PM';
        TIME_OPTIONS.push({ val: `${String(h).padStart(2, '0')}:${m}`, label: `${hour12}:${m} ${ampm}` });
    }
}
const timeLabel = (val) => TIME_OPTIONS.find(o => o.val === val)?.label || val || '—';

const EMPTY_FORM = {
    name: '',
    abreviatura: '',
    instructorId: '',
    estado: 'activo',
    coverUrl: '',
    masterclassTime: '18:00',
    masterclassHours: 1,
    totalMasterclasses: 9,
    sessionTime: '20:00',
    regularHours: 2,
    totalSessions: 6,
};

const isMc = (s) => s.type === 'masterclass';
const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
const LS_VIEW = 'hx-courses-view';
const todayStart = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

// Reduce la imagen a máx. 1920px de ancho (1920x1080) en JPEG para que la portada pese poco.
const compressImage = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
        const scale = Math.min(1, 1920 / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        canvas.toBlob(b => (b ? resolve(b) : reject(new Error('No se pudo procesar la imagen'))), 'image/jpeg', 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Imagen no válida')); };
    img.src = url;
});

const uploadCover = async (courseId, file) => {
    const blob = await compressImage(file);
    const r = storageRef(storage, `courses/${courseId}/cover_${Date.now()}.jpg`);
    await uploadBytes(r, blob, { contentType: 'image/jpeg' });
    return getDownloadURL(r);
};

const CoursesPage = () => {
    const [courses, setCourses] = useState([]);
    const [instructors, setInstructors] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);

    // Panel derecho: null | 'edit' | 'vis'
    const [panel, setPanel] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [coverFile, setCoverFile] = useState(null);
    const [coverPreview, setCoverPreview] = useState('');
    const [saving, setSaving] = useState(false);
    const fileInput = useRef(null);

    const [visCourse, setVisCourse] = useState(null);
    const [visEvents, setVisEvents] = useState(null);
    const [visBusy, setVisBusy] = useState(null);
    const [openPast, setOpenPast] = useState({});

    const [tab, setTab] = useState('activo');
    const [q, setQ] = useState('');
    const [instructorFilter, setInstructorFilter] = useState('all');
    const [view, setView] = useState(() => {
        try { return localStorage.getItem(LS_VIEW) || 'table'; } catch { return 'table'; }
    });

    // Real-time: cursos; carga inicial de instructores y eventos (incluye ocultos)
    useEffect(() => {
        let active = true;
        setLoading(true);
        const unsubscribe = subscribeToCalendarCourses((data) => {
            if (!active) return;
            setCourses(data);
            setLoading(false);
        });
        getInstructors().then(d => { if (active) setInstructors(d); }).catch(() => {});
        getSchedulesIncludingHidden().then(d => { if (active) setSchedules(d); }).catch(() => {});
        return () => { active = false; unsubscribe(); };
    }, []);

    // Esc cierra el panel
    useEffect(() => {
        if (!panel) return undefined;
        const onKey = (e) => { if (e.key === 'Escape' && !saving && !visBusy) closePanel(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [panel, saving, visBusy]); // eslint-disable-line react-hooks/exhaustive-deps

    // Libera la vista previa local de la portada
    useEffect(() => () => { if (coverPreview.startsWith('blob:')) URL.revokeObjectURL(coverPreview); }, [coverPreview]);

    const changeView = (v) => {
        setView(v);
        try { localStorage.setItem(LS_VIEW, v); } catch { /* sin storage */ }
    };

    const loadData = async () => {
        try {
            const [cData, iData] = await Promise.all([getCourses(), getInstructors()]);
            setCourses(cData);
            setInstructors(iData);
        } catch (error) {
            console.error(error);
            toast.error('Error cargando datos');
        }
    };
    const refreshSchedules = () => getSchedulesIncludingHidden().then(setSchedules).catch(() => {});

    const instById = useMemo(() => Object.fromEntries(instructors.map(i => [i.id, i])), [instructors]);

    // Estadística por curso, separando lo que viene (futMc/futCl) del historial
    // (pastMc/pastCl). Los eventos ocultos y cancelados no cuentan como programados.
    const stats = useMemo(() => {
        const now = new Date();
        const map = {};
        schedules.forEach(s => {
            if (!s.courseId || s.status === 'cancelled') return;
            const st = map[s.courseId] || (map[s.courseId] = { futMc: 0, futCl: 0, pastMc: 0, pastCl: 0, next: null, lastEnd: null, hidden: 0 });
            if (s.hidden === true) { st.hidden++; return; }
            const valid = s.start instanceof Date && !Number.isNaN(s.start.getTime());
            const end = s.end instanceof Date && !Number.isNaN(s.end.getTime()) ? s.end : s.start;
            if (valid && end >= now) {
                if (isMc(s)) st.futMc++; else st.futCl++;
                if (!st.next || s.start < st.next.start) st.next = s;
                if (!st.lastEnd || end > st.lastEnd) st.lastEnd = end;
            } else if (isMc(s)) st.pastMc++; else st.pastCl++;
        });
        return map;
    }, [schedules]);

    // Estado real del curso según lo que tiene programado a futuro.
    const statusOf = (c) => {
        if (c.estado === 'inactivo') return 'inactivo';
        const st = stats[c.id];
        if (st?.futMc > 0) return 'activo';
        if (st?.futCl > 0) return 'proceso';
        return 'sinprog';
    };

    const counts = useMemo(() => {
        const n = { activo: 0, proceso: 0, sinprog: 0, inactivo: 0 };
        courses.forEach(c => { n[statusOf(c)]++; });
        const act = courses.filter(c => statusOf(c) === 'activo');
        return {
            ...n,
            todos: courses.length,
            futMc: act.reduce((t, c) => t + (stats[c.id]?.futMc || 0), 0),
        };
    }, [courses, stats]); // eslint-disable-line react-hooks/exhaustive-deps

    const visible = useMemo(() => {
        const term = q.trim().toLowerCase();
        return courses.filter(c => {
            if (tab !== 'todos' && statusOf(c) !== tab) return false;
            if (instructorFilter === 'none' && c.instructorId) return false;
            if (instructorFilter !== 'all' && instructorFilter !== 'none' && c.instructorId !== instructorFilter) return false;
            if (!term) return true;
            const inst = instById[c.instructorId]?.name || '';
            return `${c.name} ${c.abreviatura || ''} ${inst}`.toLowerCase().includes(term);
        });
    }, [courses, tab, q, instructorFilter, instById, stats]); // eslint-disable-line react-hooks/exhaustive-deps

    const instructorOptions = useMemo(() => instructors
        .map(i => ({ ...i, n: courses.filter(c => c.instructorId === i.id).length }))
        .filter(i => i.n > 0)
        .sort((a, b) => a.name.localeCompare(b.name)), [instructors, courses]);

    // Opciones para los desplegables con buscador (HxSelect)
    const filterOptions = useMemo(() => [
        { value: 'all', label: 'Todos los instructores', icon: Users, count: courses.length },
        { value: 'none', label: 'Sin instructor', icon: UserX, count: courses.filter(c => !c.instructorId).length },
        ...instructorOptions.map(i => ({ value: i.id, label: i.name, sub: i.email || undefined, color: i.color || '#94a3b8', photo: i.photoURL || undefined, count: i.n })),
    ], [instructorOptions, courses]);
    const pickInstructorOptions = useMemo(() => [...instructors]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(i => ({ value: i.id, label: i.name, sub: i.email || undefined, color: i.color || '#94a3b8', photo: i.photoURL || undefined })), [instructors]);

    // ── Panel: editar / nuevo ─────────────────────────────────────
    const resetCover = (url = '') => { setCoverFile(null); setCoverPreview(url); };

    const closePanel = () => { setPanel(null); setEditingId(null); setVisCourse(null); };

    const openNew = () => {
        setEditingId(null);
        setFormData(EMPTY_FORM);
        resetCover('');
        setVisCourse(null);
        setPanel('edit');
    };

    const handleEdit = (course) => {
        setEditingId(course.id);
        setFormData({
            name: course.name || course.title,
            abreviatura: course.abreviatura || '',
            instructorId: course.instructorId || '',
            coverUrl: course.coverUrl || '',
            hasRegular: course.hasRegular ?? (course.type === 'regular'),
            hasMasterclass: course.hasMasterclass ?? (course.type === 'masterclass'),
            regularHours: course.regularHours || course.hoursPerSession || 2,
            regularFrequency: course.regularFrequency || course.sessionsPerWeek || 2,
            masterclassHours: course.masterclassHours || course.hoursPerSession || 3,
            masterclassGap: course.masterclassGap || course.gapDays || 7,
            intensidadComercial: course.intensidadComercial || 'media',
            rendimientoPublicidad: course.rendimientoPublicidad || 50,
            leadsActivos: course.leadsActivos || 0,
            conversionRate: course.conversionRate || 0,
            masterclassDays: course.masterclassDays || [],
            totalMasterclasses: course.totalMasterclasses || 9,
            masterclassTime: course.masterclassTime || '18:00',
            sessionTime: course.sessionTime || '20:00',
            totalSessions: course.totalSessions || 6,
            estado: course.estado || 'activo',
        });
        resetCover(course.coverUrl || '');
        setVisCourse(null);
        setPanel('edit');
    };

    const pickCover = (file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast.error('Elige una imagen'); return; }
        setCoverFile(file);
        setCoverPreview(URL.createObjectURL(file));
    };
    const removeCover = () => { resetCover(''); setFormData(f => ({ ...f, coverUrl: '' })); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;
        if (!formData.instructorId) { toast.error('Elige el instructor del curso'); return; }
        setSaving(true);
        try {
            const payload = {
                ...formData,
                // Mantener title sincronizado con name (lo leen WordPress y el bot)
                title: formData.name || formData.title || '',
                hasRegular: true,
                hasMasterclass: true,
                regularHours: Number(formData.regularHours) || 2,
                regularFrequency: Number(formData.regularFrequency) || 2,
                masterclassHours: Number(formData.masterclassHours) || 3,
                masterclassGap: Number(formData.masterclassGap) || 7,
                rendimientoPublicidad: Number(formData.rendimientoPublicidad) || 50,
                leadsActivos: Number(formData.leadsActivos) || 0,
                conversionRate: Number(formData.conversionRate) || 0,
                totalMasterclasses: Number(formData.totalMasterclasses) || 9,
                totalSessions: Number(formData.totalSessions) || 6,
                masterclassTime: formData.masterclassTime || '18:00',
                sessionTime: formData.sessionTime || '20:00',
            };
            delete payload.startDate;
            delete payload.fase;
            delete payload.modo;
            delete payload.masterphaseDuration;

            if (editingId) {
                if (coverFile) payload.coverUrl = await uploadCover(editingId, coverFile);
                const existing = courses.find(c => c.id === editingId);
                const oldName = existing?.name || existing?.title || '';
                const newName = payload.name || payload.title || '';
                const oldEstado = existing?.estado || 'activo';
                const newEstado = payload.estado || 'activo';
                await updateCourse(editingId, payload);
                toast.success('Curso actualizado');
                if (oldName && newName && oldName !== newName) {
                    try {
                        const updated = await cascadeRenameCourseEvents(editingId, oldName, newName);
                        if (updated > 0) toast(`${updated} evento(s) actualizados`, { icon: '🔄' });
                    } catch (err) {
                        console.error('Cascade rename falló:', err);
                    }
                }
                if (oldEstado !== newEstado) await applyVisibility(editingId, newEstado === 'inactivo');
                setFormData(f => ({ ...f, coverUrl: payload.coverUrl || '' }));
                resetCover(payload.coverUrl || '');
            } else {
                const created = await createCourse(payload);
                let newCover = '';
                if (coverFile && created?.id) {
                    try {
                        newCover = await uploadCover(created.id, coverFile);
                        await updateCourse(created.id, { coverUrl: newCover });
                    } catch (err) {
                        console.error(err);
                        toast.error('El curso se creó, pero la portada no se pudo subir');
                    }
                }
                toast.success('Curso creado');
                // Queda abierto en modo edición del curso recién creado
                if (created?.id) {
                    setEditingId(created.id);
                    setFormData(f => ({ ...f, coverUrl: newCover }));
                    resetCover(newCover);
                } else {
                    closePanel();
                }
            }
            loadData();
        } catch (error) {
            console.error(error);
            toast.error('Error al guardar curso');
        } finally {
            setSaving(false);
        }
    };

    const applyVisibility = async (courseId, hide) => {
        try {
            const n = await setCourseSchedulesHidden(courseId, hide);
            toast(hide
                ? `${n} evento(s) ocultos del calendario (guardados)`
                : `${n} evento(s) restaurados en el calendario`, { icon: hide ? '🙈' : '👁️' });
            refreshSchedules();
        } catch (err) {
            console.error('No se pudo actualizar visibilidad de clases:', err);
        }
    };

    const toggleEstado = async (course) => {
        const newEstado = course.estado === 'inactivo' ? 'activo' : 'inactivo';
        try {
            await updateCourse(course.id, { estado: newEstado });
            toast.success(newEstado === 'inactivo' ? 'Curso desactivado' : 'Curso activado');
            await applyVisibility(course.id, newEstado === 'inactivo');
            loadData();
        } catch {
            toast.error('Error al cambiar el estado');
        }
    };

    const handleDelete = async (course) => {
        if (!window.confirm(`¿Seguro que deseas eliminar "${course.name}"?`)) return;
        try {
            await deleteCourse(course.id);
            toast.success('Curso eliminado');
            if (editingId === course.id || visCourse?.id === course.id) closePanel();
            loadData();
        } catch {
            toast.error('Error al eliminar');
        }
    };

    // ── Panel: visibilidad por grupo ─────────────────────────────
    const openVisibility = async (course) => {
        setVisCourse(course);
        setEditingId(null);
        setVisEvents(null);
        setOpenPast({});
        setPanel('vis');
        try { setVisEvents(await getCourseSchedulesAll(course.id)); }
        catch { toast.error('No se pudieron cargar los eventos'); setVisEvents([]); }
    };

    // Filas por grupo con conteos (visibles/ocultos/futuros) y si el grupo ya terminó.
    const visGroups = useMemo(() => {
        if (!visEvents) return [];
        const t0 = todayStart();
        const map = {};
        visEvents.forEach(ev => {
            if (ev.status === 'cancelled') return;
            const g = eventGroupOf(ev);
            const row = map[g] || (map[g] = { group: g, mc: { vis: 0, hid: 0, futVis: 0 }, cl: { vis: 0, hid: 0, futVis: 0 }, first: null, last: null });
            const cell = isMc(ev) ? row.mc : row.cl;
            if (ev.hidden === true) cell.hid++; else { cell.vis++; if (ev.start >= t0) cell.futVis++; }
            if (!row.first || ev.start < row.first) row.first = ev.start;
            if (!row.last || ev.start > row.last) row.last = ev.start;
        });
        return Object.values(map)
            .map(r => ({ ...r, past: !!r.last && r.last < t0, ongoing: !!r.first && r.first < t0 && r.last >= t0 }))
            .sort((a, b) => a.group - b.group);
    }, [visEvents]);
    const pastCount = visGroups.filter(g => g.past).length;

    // hide=true oculta desde hoy; hide=false restaura todos los ocultos de esa parte.
    const applyPartial = async (group, kind, hide) => {
        const key = `${group}-${kind}-${hide}`;
        setVisBusy(key);
        try {
            const n = await setCourseSchedulesHiddenWhere(visCourse.id, { group, kind, fromDate: hide ? todayStart() : null }, hide);
            const what = kind === 'mc' ? 'masterclass(es)' : kind === 'cl' ? 'clase(s)' : 'evento(s)';
            toast.success(n === 0 ? 'No había eventos que cambiar'
                : hide ? `${n} ${what} del Grupo ${group} ocultas desde hoy` : `${n} ${what} del Grupo ${group} restauradas`);
            setVisEvents(await getCourseSchedulesAll(visCourse.id));
            refreshSchedules();
        } catch (err) {
            console.error(err);
            toast.error('No se pudo cambiar la visibilidad');
        } finally {
            setVisBusy(null);
        }
    };

    const set = (k) => (e) => setFormData(f => ({ ...f, [k]: e.target.value }));
    const selectedId = panel === 'edit' ? editingId : panel === 'vis' ? visCourse?.id : null;

    // ── Celdas ────────────────────────────────────────────────
    const instCell = (c) => {
        const inst = instById[c.instructorId];
        if (!inst) return <span className="pill st-draft"><UserX size={12} /> Sin instructor</span>;
        return <span className="who"><span className="dot" style={{ background: inst.color || '#6b7280' }} />{inst.name}</span>;
    };
    // Etiquetas con la paleta de beta: rojo vivo (punto que late) = captando con MC,
    // celeste = dictando clases, ámbar = sin fechas, pizarra = desactivado.
    const STATUS = {
        activo: { label: 'Activo', cls: 'st-live', tip: 'Tiene masterclasses por venir' },
        proceso: { label: 'En proceso', cls: 'st-rec', tip: 'Ya no tiene masterclasses; quedan clases por culminar' },
        sinprog: { label: 'Sin programar', cls: 'st-draft', tip: 'No tiene fechas futuras' },
        inactivo: { label: 'Inactivo', cls: 'st-int', tip: 'Desactivado: sus eventos están ocultos' },
    };
    const estadoPill = (c, onCover = false) => {
        const cfg = STATUS[statusOf(c)];
        return <span className={`pill ${cfg.cls}${onCover ? ' on-cover' : ''}`} title={cfg.tip}><span className="dot" />{cfg.label}</span>;
    };
    const nextCell = (c) => {
        const n = stats[c.id]?.next;
        if (!n) return <span className="muted">—</span>;
        return (
            <span>
                <b style={{ fontWeight: 600 }}>{format(n.start, 'EEE d MMM', { locale: es })}</b>
                <span className="muted"> · {format(n.start, 'h:mm a')} · {isMc(n) ? 'MC' : 'CL'}</span>
            </span>
        );
    };
    const historyText = (st) => {
        const parts = [];
        if (st?.pastMc) parts.push(`${st.pastMc} MC`);
        if (st?.pastCl) parts.push(`${st.pastCl} CL`);
        return parts.length ? `Historial: ${parts.join(' · ')} pasadas` : '';
    };
    const historyLine = (st) => (historyText(st) || st?.hidden > 0) ? (
        <div className="muted" style={{ fontSize: 11.5, marginTop: 3, whiteSpace: 'nowrap' }}>
            {historyText(st)}
            {st?.hidden > 0 && <span title="Eventos ocultos (desactivados)">{historyText(st) ? ' · ' : ''}<EyeOff size={10} style={{ verticalAlign: '-1px' }} /> {st.hidden} ocultos</span>}
        </div>
    ) : null;
    const mclCell = (c) => {
        const st = stats[c.id];
        const fut = (st?.futMc || 0) + (st?.futCl || 0);
        return (
            <div>
                {fut > 0 ? (
                    <span className="mcl">
                        {st.futMc > 0 && <span title="Masterclasses por venir">{st.futMc} <em className="k-mc">MC</em></span>}
                        {st.futCl > 0 && <span title="Clases por venir">{st.futCl} <em className="k-cl">CL</em></span>}
                    </span>
                ) : <span className="muted" style={{ fontSize: 12.5 }}>Nada programado</span>}
                {historyLine(st)}
            </div>
        );
    };
    const untilCell = (c) => {
        const st = stats[c.id];
        if (!st?.lastEnd || c.estado === 'inactivo') return null;
        return <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>Termina {format(st.lastEnd, 'd MMM', { locale: es })}</div>;
    };
    const actions = (c) => (
        <div className="row-acts" onClick={e => e.stopPropagation()}>
            <button type="button" className="ibtn" title="Editar" onClick={() => handleEdit(c)}><Pencil size={15} /></button>
            {c.estado === 'inactivo'
                ? <button type="button" className="ibtn good" title="Activar curso" onClick={() => toggleEstado(c)}><Play size={15} /></button>
                : <button type="button" className="ibtn" title="Desactivar por grupo o tipo" onClick={() => openVisibility(c)}><EyeOff size={15} /></button>}
            <button type="button" className="ibtn bad" title="Eliminar" onClick={() => handleDelete(c)}><Trash2 size={15} /></button>
        </div>
    );
    const square = (c) => c.coverUrl
        ? <img className="thumb169" src={c.coverUrl} alt="" />
        : <span className="sq" style={{ background: instById[c.instructorId]?.color || '#94a3b8' }}>{initials(c.abreviatura || c.name)}</span>;

    const fmtRange = (r) => `${r.first ? format(r.first, 'd MMM', { locale: es }) : ''} – ${r.last ? format(r.last, 'd MMM yyyy', { locale: es }) : ''}`;

    // ── Render de los paneles ─────────────────────────────────
    const editPanel = (
        <form className="hx-drawer hx-panel" onSubmit={handleSubmit}>
            <div className="d-head">
                <span style={{ width: 38, height: 38, borderRadius: 10, display: 'grid', placeItems: 'center', background: 'var(--brand-soft)', color: 'var(--brand-ink)', flexShrink: 0 }}><BookOpen size={18} /></span>
                <div style={{ minWidth: 0 }}>
                    <h2>{editingId ? 'Editar curso' : 'Nuevo curso'}</h2>
                    <p>{editingId ? formData.name : 'Se mostrará en el calendario y en WordPress'}</p>
                </div>
                <button type="button" className="ibtn x" onClick={closePanel} aria-label="Cerrar"><X size={18} /></button>
            </div>

            <div className="d-body">
                <div className="sec">
                    <div className="sec-h">Portada</div>
                    <input ref={fileInput} type="file" accept="image/*" hidden onChange={e => { pickCover(e.target.files?.[0]); e.target.value = ''; }} />
                    {coverPreview ? (
                        <div className="cover">
                            <img src={coverPreview} alt="Portada del curso" />
                            <div className="cover-acts">
                                <button type="button" className="btn" onClick={() => fileInput.current?.click()}><ImagePlus size={15} /> Cambiar</button>
                                <button type="button" className="btn" style={{ color: 'var(--bad)' }} onClick={removeCover}><Trash2 size={15} /> Quitar</button>
                            </div>
                            {coverFile && <span className="cover-tag">Se subirá al guardar</span>}
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="cover-drop"
                            onClick={() => fileInput.current?.click()}
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => { e.preventDefault(); pickCover(e.dataTransfer.files?.[0]); }}
                        >
                            <ImagePlus size={22} />
                            <b>Añadir portada</b>
                            <small>Haz clic o arrastra una imagen (16:9 recomendado)</small>
                        </button>
                    )}
                </div>

                <div className="sec">
                    <div className="sec-h">Datos del curso</div>
                    <label className="f">
                        <span>Nombre del curso</span>
                        <input className="inp" required value={formData.name} onChange={set('name')} placeholder="Ej. Revit Estructuras" />
                    </label>
                    <label className="f">
                        <span>Abreviatura <small>— opcional, se muestra en el calendario</small></span>
                        <input className="inp" value={formData.abreviatura || ''} onChange={set('abreviatura')} placeholder="Ej. DISEÑO EST., REVIT BIM" />
                    </label>
                    <div className="f">
                        <span>Instructor asignado</span>
                        <HxSelect
                            className="full"
                            value={formData.instructorId}
                            onChange={v => setFormData(f => ({ ...f, instructorId: v }))}
                            options={pickInstructorOptions}
                            placeholder="Selecciona un instructor…"
                            searchPlaceholder="Buscar instructor…"
                            icon={Users}
                            ariaLabel="Instructor asignado"
                        />
                    </div>
                    <div className="f">
                        <span>Estado <small>— Activo, En proceso o Sin programar se calcula solo con las fechas futuras</small></span>
                        <div className="opt">
                            <button type="button" className={formData.estado !== 'inactivo' ? 'on' : ''} onClick={() => setFormData(f => ({ ...f, estado: 'activo' }))}>
                                <span style={{ background: 'var(--ok)', width: 8, height: 8, borderRadius: '50%' }} /> Activo
                            </button>
                            <button type="button" className={formData.estado === 'inactivo' ? 'on' : ''} onClick={() => setFormData(f => ({ ...f, estado: 'inactivo' }))}>
                                <span style={{ background: 'var(--ink-3)', width: 8, height: 8, borderRadius: '50%' }} /> Inactivo
                            </button>
                        </div>
                    </div>
                </div>

                <div className="sec">
                    <div className="sec-h">Horarios por defecto</div>
                    <div className="box" style={{ marginBottom: 12 }}>
                        <div className="box-h"><Radio size={15} style={{ color: 'var(--ok)' }} /> Masterclass</div>
                        <div className="three">
                            <label className="f"><span>Hora inicio</span>
                                <select className="inp" required value={formData.masterclassTime} onChange={set('masterclassTime')}>
                                    {TIME_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                                </select>
                            </label>
                            <label className="f"><span>Duración (h)</span>
                                <input className="inp" type="number" min="1" max="12" step="0.5" required value={formData.masterclassHours} onChange={set('masterclassHours')} />
                            </label>
                            <label className="f"><span>MC por grupo</span>
                                <input className="inp" type="number" min="1" max="50" required value={formData.totalMasterclasses} onChange={set('totalMasterclasses')} />
                            </label>
                        </div>
                    </div>
                    <div className="box">
                        <div className="box-h"><GraduationCap size={15} style={{ color: 'var(--brand)' }} /> Clase</div>
                        <div className="three">
                            <label className="f"><span>Hora inicio</span>
                                <select className="inp" required value={formData.sessionTime} onChange={set('sessionTime')}>
                                    {TIME_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                                </select>
                            </label>
                            <label className="f"><span>Duración (h)</span>
                                <input className="inp" type="number" min="1" max="12" step="0.5" required value={formData.regularHours} onChange={set('regularHours')} />
                            </label>
                            <label className="f"><span>Clases por grupo</span>
                                <input className="inp" type="number" min="1" max="50" required value={formData.totalSessions} onChange={set('totalSessions')} />
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <div className="d-foot">
                {editingId && (
                    <button type="button" className="btn" style={{ marginRight: 'auto' }} onClick={() => openVisibility(courses.find(c => c.id === editingId) || { id: editingId, name: formData.name })}>
                        <EyeOff size={15} /> Grupos
                    </button>
                )}
                <button type="button" className="btn" onClick={closePanel}>Cerrar</button>
                <button type="submit" className="btn pri" disabled={saving}>{saving ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Crear curso'}</button>
            </div>
        </form>
    );

    const groupBox = (row) => (
        <div className="box">
            <div className="box-h" style={{ flexWrap: 'wrap' }}>
                Grupo {row.group}
                <span style={{ fontWeight: 500, fontSize: 12, color: 'var(--ink-3)' }}>{fmtRange(row)}</span>
                {row.ongoing && <span className="pill t-blue" style={{ fontSize: 10.5, padding: '1px 7px', borderRadius: 99, fontWeight: 700 }}>En curso</span>}
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    {(row.mc.futVis + row.cl.futVis) > 0 && (
                        <button type="button" className="btn" style={{ height: 30, fontSize: 12.5, padding: '0 10px' }} disabled={!!visBusy}
                            onClick={() => applyPartial(row.group, null, true)}>
                            <EyeOff size={13} /> Ocultar grupo
                        </button>
                    )}
                    {(row.mc.hid + row.cl.hid) > 0 && (
                        <button type="button" className="btn" style={{ height: 30, fontSize: 12.5, padding: '0 10px' }} disabled={!!visBusy}
                            onClick={() => applyPartial(row.group, null, false)}>
                            <Eye size={13} /> Mostrar grupo
                        </button>
                    )}
                </span>
            </div>
            {[['mc', 'Masterclass', Radio, 'var(--ok)'], ['cl', 'Clases', GraduationCap, 'var(--brand)']].map(([kind, label, Icon, color]) => {
                const c = row[kind];
                if (c.vis + c.hid === 0) return null;
                const busyHide = visBusy === `${row.group}-${kind}-true`;
                const busyShow = visBusy === `${row.group}-${kind}-false`;
                return (
                    <div key={kind} className="blk">
                        <Icon size={15} style={{ color }} />
                        <b style={{ fontWeight: 600 }}>{label}</b>
                        <small>
                            {c.vis} visible{c.vis === 1 ? '' : 's'}
                            {c.hid > 0 && <span style={{ color: 'var(--warn)', fontWeight: 600 }}> · {c.hid} oculta{c.hid === 1 ? '' : 's'}</span>}
                        </small>
                        <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                            {c.hid > 0 && (
                                <button type="button" className="btn" style={{ height: 28, fontSize: 12, padding: '0 9px' }} disabled={!!visBusy}
                                    onClick={() => applyPartial(row.group, kind, false)}>
                                    <Eye size={13} /> {busyShow ? '…' : 'Mostrar'}
                                </button>
                            )}
                            {c.futVis > 0 && (
                                <button type="button" className="btn" style={{ height: 28, fontSize: 12, padding: '0 9px', color: 'var(--bad)' }} disabled={!!visBusy}
                                    onClick={() => applyPartial(row.group, kind, true)} title={`Oculta ${c.futVis} desde hoy`}>
                                    <EyeOff size={13} /> {busyHide ? '…' : `Ocultar ${c.futVis}`}
                                </button>
                            )}
                        </span>
                    </div>
                );
            })}
        </div>
    );

    const visPanel = visCourse && (
        <div className="hx-drawer hx-panel">
            <div className="d-head">
                <span style={{ width: 38, height: 38, borderRadius: 10, display: 'grid', placeItems: 'center', background: 'var(--warn-soft)', color: 'var(--warn)', flexShrink: 0 }}><EyeOff size={18} /></span>
                <div style={{ minWidth: 0 }}>
                    <h2>Desactivar por grupo</h2>
                    <p>{visCourse.name}</p>
                </div>
                <button type="button" className="ibtn x" onClick={closePanel} aria-label="Cerrar"><X size={18} /></button>
            </div>

            <div className="d-body">
                <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '16px 0 4px', lineHeight: 1.5 }}>
                    Oculta solo la parte que quieras dar de baja. Se ocultan los eventos <b>desde hoy</b>; los pasados quedan como historial.
                    Lo oculto sale del calendario, de WordPress y del bot, pero queda guardado y se puede restaurar.
                </p>

                {visEvents === null ? (
                    <p style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 20 }}>Cargando eventos…</p>
                ) : visGroups.length === 0 ? (
                    <p style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 20 }}>Este curso no tiene eventos programados.</p>
                ) : (
                    <>
                        {pastCount > 0 && (
                            <div className="sec">
                                <div className="sec-h"><History size={12} /> Grupos pasados · {pastCount}</div>
                                {visGroups.filter(g => g.past).map(row => {
                                    const open = !!openPast[row.group];
                                    return open ? (
                                        <div key={row.group} style={{ marginBottom: 8 }}>
                                            <button type="button" className="past-row open" onClick={() => setOpenPast(p => ({ ...p, [row.group]: false }))}>
                                                <ChevronRight size={14} className="chev" /> Grupo {row.group} · Pasado
                                            </button>
                                            {groupBox(row)}
                                        </div>
                                    ) : (
                                        <button key={row.group} type="button" className="past-row" onClick={() => setOpenPast(p => ({ ...p, [row.group]: true }))}>
                                            <ChevronRight size={14} className="chev" />
                                            <b>Grupo {row.group}</b>
                                            <span className="muted">{fmtRange(row)}</span>
                                            <span className="past-tag">Pasado</span>
                                            <span className="muted" style={{ marginLeft: 'auto' }}>
                                                {row.mc.vis + row.mc.hid} MC · {row.cl.vis + row.cl.hid} CL
                                                {(row.mc.hid + row.cl.hid) > 0 && <span style={{ color: 'var(--warn)' }}> · {row.mc.hid + row.cl.hid} ocultas</span>}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {visGroups.filter(g => !g.past).length > 0 && (
                            <div className="sec">
                                <div className="sec-h">Grupos en curso y próximos</div>
                                {visGroups.filter(g => !g.past).map(row => (
                                    <div key={row.group} style={{ marginBottom: 12 }}>{groupBox(row)}</div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="d-foot" style={{ justifyContent: 'space-between' }}>
                <button type="button" className="btn" style={{ color: 'var(--bad)' }} disabled={!!visBusy}
                    onClick={async () => {
                        if (!window.confirm(`¿Desactivar "${visCourse.name}" completo? Se ocultan TODOS sus eventos (quedan guardados).`)) return;
                        const course = visCourse;
                        closePanel();
                        await toggleEstado(course);
                    }}>
                    <CirclePause size={15} /> Desactivar curso completo
                </button>
                <button type="button" className="btn pri" onClick={closePanel}>Listo</button>
            </div>
        </div>
    );

    return (
        <div className={`hx-split${panel ? ' has-panel' : ''}`}>
            <div className="hx-page">
                <div className="crumb">Calendario › Cursos</div>
                <div className="p-head">
                    <div>
                        <h1>Cursos</h1>
                        <p>{counts.todos} cursos · {counts.activo} activos · {counts.proceso} en proceso · {counts.sinprog} sin programar · {counts.inactivo} inactivos</p>
                    </div>
                    <div className="acts">
                        <button type="button" className="btn pri" onClick={openNew}><Plus size={16} /> Nuevo curso</button>
                    </div>
                </div>

                <div className="kpis">
                    <button type="button" className={`card kpi kpi-btn${tab === 'activo' ? ' on' : ''}`} onClick={() => setTab('activo')}>
                        <div className="lbl"><span className="ico t-green"><CircleCheck size={15} /></span>Activos</div>
                        <div className="num">{loading ? '–' : counts.activo}</div>
                        <div className="hint">Con masterclasses por venir · {counts.futMc} MC</div>
                    </button>
                    <button type="button" className={`card kpi kpi-btn${tab === 'proceso' ? ' on' : ''}`} onClick={() => setTab('proceso')}>
                        <div className="lbl"><span className="ico t-blue"><GraduationCap size={15} /></span>En proceso</div>
                        <div className="num">{loading ? '–' : counts.proceso}</div>
                        <div className="hint">Sin masterclass, con clases por culminar</div>
                    </button>
                    <button type="button" className={`card kpi kpi-btn${tab === 'sinprog' ? ' on' : ''}`} onClick={() => setTab('sinprog')}>
                        <div className="lbl"><span className="ico t-amber"><CalendarClock size={15} /></span>Sin programar</div>
                        <div className="num">{loading ? '–' : counts.sinprog}</div>
                        <div className="hint">Sin fechas futuras</div>
                    </button>
                    <button type="button" className={`card kpi kpi-btn${tab === 'inactivo' ? ' on' : ''}`} onClick={() => setTab('inactivo')}>
                        <div className="lbl"><span className="ico t-gray"><CirclePause size={15} /></span>Inactivos</div>
                        <div className="num">{loading ? '–' : counts.inactivo}</div>
                        <div className="hint">Desactivados, eventos guardados</div>
                    </button>
                </div>

                <div className="toolbar">
                    <div className="tabs">
                        {[['activo', 'Activos', counts.activo], ['proceso', 'En proceso', counts.proceso], ['sinprog', 'Sin programar', counts.sinprog], ['inactivo', 'Inactivos', counts.inactivo], ['todos', 'Todos', counts.todos]].map(([k, l, n]) => (
                            <button key={k} type="button" className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{l} <em>{n}</em></button>
                        ))}
                    </div>
                    <HxSelect
                        value={instructorFilter}
                        onChange={setInstructorFilter}
                        options={filterOptions}
                        searchPlaceholder="Buscar instructor…"
                        icon={Users}
                        ariaLabel="Filtrar por instructor"
                    />
                    <label className="search">
                        <Search size={15} />
                        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por curso, abreviatura o instructor…" />
                        {q && <button type="button" className="ibtn" style={{ width: 22, height: 22 }} onClick={() => setQ('')} aria-label="Limpiar"><X size={13} /></button>}
                    </label>
                    <div className="seg">
                        <button type="button" className={view === 'table' ? 'on' : ''} onClick={() => changeView('table')} title="Vista tabla"><LayoutList size={16} /></button>
                        <button type="button" className={view === 'cards' ? 'on' : ''} onClick={() => changeView('cards')} title="Vista tarjetas"><LayoutGrid size={16} /></button>
                    </div>
                </div>

                {loading ? (
                    <div className="card"><div className="skel" /><div className="skel" /><div className="skel" /></div>
                ) : visible.length === 0 ? (
                    <div className="card empty">
                        {q || instructorFilter !== 'all' ? 'Ningún curso coincide con los filtros.' : 'Aún no hay cursos en esta pestaña.'}
                    </div>
                ) : view === 'table' ? (
                    <div className="card tbl-wrap">
                        <table className="tbl">
                            <thead>
                                <tr>
                                    <th>Curso</th>
                                    <th>Instructor</th>
                                    <th>Por venir</th>
                                    <th className="wide-only">Próxima sesión</th>
                                    <th className="wide-only">Horario base</th>
                                    <th>Estado</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(c => (
                                    <tr key={c.id} className={`click${selectedId === c.id ? ' is-sel' : ''}`} onClick={() => handleEdit(c)}>
                                        <td>
                                            <div className="ttl">
                                                {square(c)}
                                                <div>
                                                    <b>{c.name}</b>
                                                    {c.abreviatura && <small>{c.abreviatura}</small>}
                                                </div>
                                            </div>
                                        </td>
                                        <td>{instCell(c)}</td>
                                        <td>{mclCell(c)}</td>
                                        <td className="wide-only" style={{ whiteSpace: 'nowrap' }}>{nextCell(c)}</td>
                                        <td className="wide-only" style={{ whiteSpace: 'nowrap', fontSize: 12.5 }}>
                                            <div><span className="muted">MC</span> {timeLabel(c.masterclassTime)} · {c.masterclassHours || 1}h</div>
                                            <div><span className="muted">CL</span> {timeLabel(c.sessionTime)} · {c.regularHours || 2}h</div>
                                        </td>
                                        <td>{estadoPill(c)}{untilCell(c)}</td>
                                        <td className="r">{actions(c)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="cgrid">
                        {visible.map(c => {
                            const st = stats[c.id];
                            const color = instById[c.instructorId]?.color || '#94a3b8';
                            return (
                                <div key={c.id} className={`card gcard${selectedId === c.id ? ' is-sel' : ''}${c.estado === 'inactivo' ? ' is-off' : ''}`}
                                    role="button" tabIndex={0} onClick={() => handleEdit(c)}
                                    onKeyDown={e => { if (e.key === 'Enter') handleEdit(c); }}>
                                    <div className={`gcover${c.coverUrl ? '' : ' empty'}`}
                                        style={c.coverUrl ? undefined : { background: `linear-gradient(140deg, ${color}, ${color}cc 60%, #0d0f14)` }}>
                                        {c.coverUrl ? <img src={c.coverUrl} alt="" /> : <span>{initials(c.abreviatura || c.name)}</span>}
                                        <div className="gpills">{estadoPill(c, true)}</div>
                                    </div>
                                    <div className="gbody">
                                        <h3 title={c.name}>{c.name}</h3>
                                        <div className="gmeta">
                                            {instById[c.instructorId]
                                                ? <><span className="dot" style={{ background: color }} />{instById[c.instructorId].name}</>
                                                : <span style={{ color: 'var(--warn)', fontWeight: 600 }}>Sin instructor</span>}
                                            {c.abreviatura && <><span className="sep" />{c.abreviatura}</>}
                                        </div>
                                        <div className="gnext">
                                            {st?.next
                                                ? <><CalendarClock size={13} /><span className="muted">Próxima</span>{nextCell(c)}</>
                                                : <span className="muted">Nada programado</span>}
                                        </div>
                                        <div className="gstats">
                                            <span title="Masterclasses por venir"><Radio size={14} /> {st?.futMc || 0} MC</span>
                                            <span title="Clases por venir"><GraduationCap size={14} /> {st?.futCl || 0} CL</span>
                                            <span style={{ marginLeft: 'auto' }}>{actions(c)}</span>
                                        </div>
                                        {historyLine(st)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {panel === 'edit' && editPanel}
            {panel === 'vis' && visPanel}
        </div>
    );
};

export default CoursesPage;
